// Convert UTC time (HH:mm) to local time for display
import { ClientAddress, Project } from "@/api";

export const utcToLocalTime = (utcTimeString: string): string => {
  if (!utcTimeString) return "";

  const today = new Date();
  const [hours, minutes] = utcTimeString.split(":").map(Number);

  // Create UTC date for today with the given time
  const utcDate = new Date(
    Date.UTC(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hours,
      minutes
    )
  );

  // Convert to local time and format as HH:mm
  const localHours = utcDate.getHours().toString().padStart(2, "0");
  const localMinutes = utcDate.getMinutes().toString().padStart(2, "0");

  return `${localHours}:${localMinutes}`;
};

export const calculateDuration = (start: string, end: string) => {
  const [startHour, startMin] = start.split(":").map(Number);
  const [endHour, endMin] = end.split(":").map(Number);
  const duration = endHour * 60 + endMin - (startHour * 60 + startMin);
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  return hours + minutes / 60;
};

export const fullProjectName = (project: Project): string => {
  // Prefer the address-based display name from the server.
  if (project.display_name && project.display_name.trim() !== "") {
    const clientPart = project.client?.name ? `${project.client.name} – ` : "";
    return `${clientPart}${project.display_name}`;
  }

  // Fallback: try to compose from client address.
  const keys: (keyof ClientAddress)[] = [
    "street",
    "zipcode",
    "city",
    "country",
  ];
  const parts = keys
    .map((k) => project.address_json?.[k] ?? project.client?.address_json?.[k])
    .filter((v) => !!v);
  if (project.client?.name) {
    parts.unshift(project.client.name);
  }
  const address = parts.join(", ");
  if (address) {
    return `${address} - ${project.name}`;
  }
  return project.name || project.code;
};

export const getDeviceLocale = (): string => {
  return navigator.language || "nl-NL";
};

export const formatNumber = (
  value: number,
  decimals: number = 1,
  locale?: string
): string => {
  const deviceLocale = locale || getDeviceLocale();
  return new Intl.NumberFormat(deviceLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Resizes a base64-dataUrl-encoded image constrained to the maximum size
 *
 * @param base64
 * @param maxSize
 */
export const resizeBase64Image = (
  base64: string,
  maxSize = 2048
): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;

      // If already within bounds, return original
      if (width <= maxSize && height <= maxSize) {
        resolve(base64);
        return;
      }

      // Only resize if larger than max
      const scale = Math.min(maxSize / width, maxSize / height);
      const newWidth = Math.round(width * scale);
      const newHeight = Math.round(height * scale);

      const canvas = document.createElement("canvas");
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext("2d");
      ctx?.drawImage(img, 0, 0, newWidth, newHeight);

      const resized = canvas.toDataURL("image/jpeg", 0.9);
      resolve(resized);
    };

    img.src = base64;
  });
};
